import { CompareSidebar } from '@/components/AssetSidebar/CompareSidebar';
import { api, getBaseUrl } from '@/services/api';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { Box, styled, Theme } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BodyText } from '@/components/BodyText';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useGetMatchScores } from '@/hooks/api/assets/useGetMatchScores';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AssetState } from '@/api/generatedApi';
import { TitleText } from '@/components/TitleText';
import { getAssetName } from '@/utils/assets';
import { CompareTable } from '@/components/ComparePage/CompareTable';
import { TextDiffViewer } from '@/components/ComparePage/TextDiff/TextDiffViewer';
import { useThemeMode } from '@/hooks/useThemeMode';
import { ComparePageObjectModel } from '@/poms/comparePageObjectModel';
import { useCompareSelection } from '@/hooks/compare/useCompareSelection';
import { isModel, isReadyForCompare, isTextDocument } from '@/utils/models';

const useStyles = createUseStyles((theme: Theme) => ({
  ComparePage_root: {
    flex: 1,
    minWidth: '400px',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  ComparePage_iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  ComparePage_fadeLoader: {
    position: 'absolute',
    top: '8px',
    left: '0',
    bottom: '8px',
    right: '8px',
    animation: '$fadeLoader 1.5s ease-in-out infinite',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '@keyframes fadeLoader': {
    '0%': {
      backgroundColor: 'transparent',
    },
    '50%': {
      backgroundColor: theme.palette.action.hover,
    },
    '100%': {
      backgroundColor: 'transparent',
    },
  },
}));

const ZeroStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  color: theme.palette.text.primary,
}));

const PageWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

const PageContent = styled(Box)({
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  width: '100%',
});

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  width: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '8px',
});

const CompareError = ({ message }: { message: string }) => (
  <ZeroStateBox data-testid={ComparePageObjectModel.ZeroStateContainer}>
    <TitleText>Error</TitleText>
    <BodyText color='text.secondary' data-testid={ComparePageObjectModel.ErrorMessage}>
      {message}
    </BodyText>
  </ZeroStateBox>
);

export const ComparePage = () => {
  const c = useStyles();
  const [searchParameters] = useSearchParams();
  const { getTenantId } = useTenants();
  const { resolvedMode } = useThemeMode();
  const [error, setError] = useState<string>();
  const iframeReference = useRef<HTMLIFrameElement>(null);
  const viewerRequestReference = useRef(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const asset1Id = searchParameters.get('asset1Id') ?? undefined;
  const asset2Id = searchParameters.get('asset2Id') ?? undefined;
  const tenant1Id = searchParameters.get('tenant1Id') ?? getTenantId();
  const tenant2Id = searchParameters.get('tenant2Id') ?? undefined;
  const { selectionPaths, selectFromTree } = useCompareSelection({ iframeReference, asset1Id, asset2Id });

  const hasBothAssetIds = !!(asset1Id && asset2Id);
  const isSameTenant = !tenant2Id || tenant1Id === tenant2Id;

  const { asset: asset1, isLoading: asset1IsLoading } = useGetAsset({ assetId: asset1Id, assetTenantId: tenant1Id });
  const { asset: asset2, isLoading: asset2IsLoading } = useGetAsset({ assetId: asset2Id, assetTenantId: tenant2Id });

  const isTextComparison = isTextDocument(asset1) && isTextDocument(asset2);
  const bothAssetsFinished = asset1?.state === AssetState.Finished && asset2?.state === AssetState.Finished;
  const bothAssetsModels = isModel(asset1) && isModel(asset2);
  const shouldFetchMatchScores =
    hasBothAssetIds && asset1Id !== asset2Id && isSameTenant && bothAssetsFinished && bothAssetsModels;

  const { matchScores } = useGetMatchScores({
    sourceAssetId: asset1Id,
    targetAssetId: asset2Id,
    assetTenantId: tenant1Id,
    enabled: shouldFetchMatchScores,
  });

  const getCompareTitle = () => {
    const names = [asset1, asset2]
      .filter((asset): asset is NonNullable<typeof asset> => asset !== undefined)
      .map(asset => getAssetName(asset));
    if (names.length === 2) {
      return `Compare ${names[0]} & ${names[1]}`;
    }
    if (names.length === 1) {
      return `Compare ${names[0]}`;
    }
    return 'Compare';
  };

  usePageTitle(getCompareTitle());

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const fetchComparisonViewer = async (requestId: number) => {
    setError(undefined);
    setIframeLoaded(false);
    try {
      const apiLocation = getBaseUrl();

      const {
        data: { token },
      } = await api.viewer.viewerToken();

      if (requestId !== viewerRequestReference.current) {
        return;
      }

      let iframeSource = `${apiLocation}/tenants/${tenant1Id}/viewer/comparison?token=${token}&asset1Id=${asset1Id}&asset2Id=${asset2Id}&theme=${resolvedMode}&parentOrigin=${encodeURIComponent(globalThis.location.origin)}`;

      if (tenant2Id) {
        iframeSource += `&tenant2Id=${tenant2Id}`;
      }

      if (iframeReference.current) {
        iframeReference.current.src = iframeSource;
      }
    } catch (error) {
      if (requestId !== viewerRequestReference.current) {
        return;
      }

      setError(error instanceof AxiosError ? error.message : 'Error fetching comparison viewer');
    }
  };
  useEffect(() => {
    // The token outlives the pair it was requested for, so a slow response for one comparison
    // must not be applied to the frame now showing another. Counting here rather than inside
    // the fetch retires an in-flight request even when the new state asks for no fetch at all.
    viewerRequestReference.current += 1;

    if (asset1Id && asset2Id && tenant1Id && !asset1IsLoading && !asset2IsLoading && !isTextComparison) {
      void fetchComparisonViewer(viewerRequestReference.current);
    }
  }, [asset1Id, asset2Id, tenant1Id, tenant2Id, asset1IsLoading, asset2IsLoading, isTextComparison]);

  useEffect(() => {
    const iframe = iframeReference.current;
    if (!iframe?.contentWindow || !iframe.src) return;
    const targetOrigin = new URL(iframe.src).origin;
    iframe.contentWindow.postMessage({ type: 'physna:theme-mode', mode: resolvedMode }, targetOrigin);
  }, [resolvedMode]);

  if (
    !asset1IsLoading &&
    !asset2IsLoading &&
    asset1 &&
    asset2 &&
    (!isReadyForCompare(asset1) || !isReadyForCompare(asset2))
  ) {
    return (
      <CompareError
        message={
          isReadyForCompare(asset1)
            ? `${getAssetName(asset2)} is ${asset2.state}`
            : `${getAssetName(asset1)} is ${asset1.state}`
        }
      />
    );
  }

  if (asset1 && asset2 && isTextDocument(asset1) !== isTextDocument(asset2)) {
    return <CompareError message='Text files can only be compared with other text files' />;
  }

  return (
    <PageWrapper>
      <PageContent>
        <CompareSidebar
          asset1Id={asset1Id}
          asset2Id={asset2Id}
          asset1TenantId={tenant1Id}
          asset2TenantId={tenant2Id}
          geometricScores={matchScores?.geometric}
          volumetricScores={matchScores?.volumetric}
          selectionPaths={selectionPaths}
          onTreeItemClick={selectFromTree}
        />
        <ContentArea>
          {!hasBothAssetIds && <CompareTable />}
          {hasBothAssetIds && isTextComparison && <TextDiffViewer leftAsset={asset1} rightAsset={asset2} />}
          {hasBothAssetIds && !isTextComparison && (
            <Box position='relative' width='100%' height='100%'>
              {!iframeLoaded && (
                <Box className={c.ComparePage_fadeLoader} data-testid={ComparePageObjectModel.LoadingIndicator}>
                  <BodyText color='text.secondary'>...</BodyText>
                </Box>
              )}
              <iframe
                ref={iframeReference}
                className={c.ComparePage_iframe}
                title={ComparePageObjectModel.ComparisonViewer}
                onLoad={handleIframeLoad}
              />
              {error && <div>{error}</div>}
            </Box>
          )}
        </ContentArea>
      </PageContent>
    </PageWrapper>
  );
};
